import { useCallback, useEffect, useMemo, useState } from 'react';
import adminService from '@wlb/shared/api/admin/adminService';
import type { ReportedContentItem } from '@wlb/shared/api/admin/adminTypes';
import { useAdminSession } from '@wlb/shared/context/AdminSessionContext';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Button } from '../components/ui/Button';
import { FormError } from '../components/ui/FormError';
import { IconButton } from '../components/ui/IconButton';
import { StateBlock } from '../components/ui/StateBlock';
import { ReportedContentCard } from '../features/moderation/ReportedContentCard';
import { ReviewReportsDialog } from '../features/moderation/ReviewReportsDialog';
import { usePagedList } from '../hooks/usePagedList';
import { useScrollReset } from '../hooks/useScrollReset';
import { PageHeader } from '../components/layout/PageHeader';
import '../components/ui/ui.css';
import '../features/posts/post-card.css';
import './pages.css';

const PAGE_SIZE = 20;

export type ModerationQueue = 'pending' | 'reviewed';

interface ModerationProps {
  /**
   * Supplied by the route rather than a select: the two queues are separate
   * places an operator links to and comes back to, so each one owns a URL.
   */
  queue: ModerationQueue;
}

export function Moderation({ queue }: ModerationProps) {
  const { t } = useTranslation('admin');
  const { adminUser } = useAdminSession();
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());
  const [reviewIntent, setReviewIntent] = useState<'approve' | 'reject' | null>(null);
  const [bulkPending, setBulkPending] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const userId = adminUser?.userId ?? 0;

  const loadPage = useCallback(
    async (page: number) => {
      const startIndex = (page - 1) * PAGE_SIZE;
      const { items, totalRecords: total } = await adminService.getReportedContent({
        userId,
        startIndex,
        pageSize: PAGE_SIZE,
        isReviewed: queue === 'reviewed',
      });
      setTotalRecords(total);
      return {
        items,
        // An empty page ends the list whatever the total claims, so a count
        // that outruns the rows cannot leave "Load more" asking forever.
        hasNextPage:
          items.length > 0 &&
          (total > 0
            ? startIndex + items.length < total
            : items.length >= PAGE_SIZE),
      };
    },
    [queue, userId],
  );

  const {
    items: rows,
    status,
    refreshing,
    loadingMore,
    hasNextPage,
    error,
    loadMore,
    reload,
  } = usePagedList(loadPage, `${queue}|${userId}`, t('common.errorTitle'));

  const feedRef = useScrollReset<HTMLDivElement>(queue);

  // The queue arrives from the route, so a switch has to drop a selection made
  // in the other queue as the new value first renders.
  useEffect(() => {
    setSelected(new Set());
  }, [queue]);

  const selectedItems = useMemo(
    () => rows.filter(row => selected.has(row.reportedPostId)),
    [rows, selected],
  );

  const refreshAfterAction = useCallback(() => {
    setSelected(new Set());
    reload();
  }, [reload]);

  const toggleRow = useCallback((key: number) => {
    setSelected(current => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected(current =>
      current.size === rows.length
        ? new Set<number>()
        : new Set(rows.map(row => row.reportedPostId)),
    );
  }, [rows]);

  const changePostVisibility = async (isActive: boolean, postIds?: number[]) => {
    const ids = postIds ?? selectedItems.map(item => item.postId).filter(id => id > 0);
    if (ids.length === 0) {
      ToastService.show({ type: 'warning', message: t('moderation.selectionRequired') });
      return;
    }
    setBulkPending(true);
    try {
      await adminService.setPostsVisibility(ids, isActive);
      ToastService.show({
        type: 'success',
        message: isActive
          ? t('moderation.toasts.postsRestored')
          : t('moderation.toasts.postsHidden'),
      });
      refreshAfterAction();
    } catch (cause) {
      ToastService.show({
        type: 'error',
        message: getUserFacingErrorMessage(cause, t('common.errorTitle')),
      });
    } finally {
      setBulkPending(false);
    }
  };

  const openReview = (intent: 'approve' | 'reject') => {
    if (selectedItems.length === 0) {
      ToastService.show({ type: 'warning', message: t('moderation.selectionRequired') });
      return;
    }
    setReviewIntent(intent);
  };

  const reviewSingle = useCallback(
    async (item: ReportedContentItem, intent: 'approve' | 'reject') => {
      try {
        await adminService.reviewReportedContent({
          isReportApproved: intent === 'approve',
          items: [item],
        });
        ToastService.show({
          type: 'success',
          message:
            intent === 'approve'
              ? t('moderation.toasts.approved')
              : t('moderation.toasts.rejected'),
        });
        refreshAfterAction();
      } catch (cause) {
        ToastService.show({
          type: 'error',
          message: getUserFacingErrorMessage(cause, t('common.errorTitle')),
        });
      }
    },
    [refreshAfterAction, t],
  );

  const hideSingle = useCallback(
    async (item: ReportedContentItem) => {
      if (item.postId <= 0) return;
      try {
        await adminService.setPostsVisibility([item.postId], false);
        ToastService.show({ type: 'success', message: t('moderation.toasts.postsHidden') });
        refreshAfterAction();
      } catch (cause) {
        ToastService.show({
          type: 'error',
          message: getUserFacingErrorMessage(cause, t('common.errorTitle')),
        });
      }
    },
    [refreshAfterAction, t],
  );

  const restoreSingle = useCallback(
    async (item: ReportedContentItem) => {
      if (item.postId <= 0) return;
      try {
        await adminService.setPostsVisibility([item.postId], true);
        ToastService.show({ type: 'success', message: t('moderation.toasts.postsRestored') });
        refreshAfterAction();
      } catch (cause) {
        ToastService.show({
          type: 'error',
          message: getUserFacingErrorMessage(cause, t('common.errorTitle')),
        });
      }
    },
    [refreshAfterAction, t],
  );

  /* Appended at the end of the column rather than stepped through: a report card
     is tall, so a numbered strip meant scrolling to the bottom and back up. A
     failed step keeps the reports already listed and retries in place. */
  const loadMoreFailed = Boolean(error) && status === 'ready';
  const loadMoreControls =
    hasNextPage || loadMoreFailed ? (
      <>
        {loadMoreFailed ? <FormError message={error} /> : null}
        {hasNextPage ? (
          <Button variant="secondary" small disabled={loadingMore} onClick={loadMore}>
            {loadingMore
              ? t('common.loading')
              : loadMoreFailed
                ? t('common.retry')
                : t('common.loadMore')}
          </Button>
        ) : null}
      </>
    ) : null;

  return (
    <div className="page">
      <PageHeader
        title={t('moderation.title')}
        subtitle={
          queue === 'reviewed'
            ? t('moderation.subtitleReviewed')
            : t('moderation.subtitlePending')
        }
        actions={
          <IconButton
            icon="refresh"
            tone="slate"
            label={t('common.refresh')}
            disabled={refreshing}
            onClick={reload}
          />
        }
      />

      {rows.length > 0 ? (
        <section className="page__toolbar" aria-label={t('common.filters')}>
          <div className="page__toolbar-field">
            <label className="ui-checkbox">
              <input
                type="checkbox"
                className="ui-table__checkbox"
                checked={selected.size === rows.length}
                onChange={toggleAll}
                aria-label={t('common.selectAll')}
              />
              {t('common.selectAll')}
            </label>
          </div>
          {totalRecords > 0 ? (
            <span className="page__toolbar-note">
              {t('common.showing', {
                from: 1,
                to: rows.length,
                total: totalRecords,
              })}
            </span>
          ) : null}
        </section>
      ) : null}

      {selected.size > 0 ? (
        <div className="page__bulk-bar">
          <span>{t('common.selectedCount', { count: selected.size })}</span>
          <div className="page__bulk-actions">
            <Button small disabled={bulkPending} onClick={() => openReview('approve')}>
              {t('moderation.actions.approve')}
            </Button>
            <Button
              variant="secondary"
              small
              disabled={bulkPending}
              onClick={() => openReview('reject')}>
              {t('moderation.actions.reject')}
            </Button>
            <Button
              variant="danger"
              small
              disabled={bulkPending}
              onClick={() => changePostVisibility(false)}>
              {t('moderation.actions.hidePosts')}
            </Button>
            <Button
              variant="secondary"
              small
              disabled={bulkPending}
              onClick={() => changePostVisibility(true)}>
              {t('moderation.actions.showPosts')}
            </Button>
          </div>
        </div>
      ) : null}

      {status === 'loading' ? (
        <div className="page__feed">
          <div className="ui-table-panel">
            <StateBlock status="loading" />
          </div>
        </div>
      ) : status === 'error' ? (
        <div className="page__feed">
          <div className="ui-table-panel">
            <StateBlock status="error" message={error} onRetry={reload} />
          </div>
        </div>
      ) : rows.length === 0 ? (
        <div className="page__feed">
          <div className="ui-table-panel">
            <StateBlock
              status="empty"
              title={t('moderation.emptyTitle')}
              message={t('moderation.emptyMessage')}
            />
          </div>
        </div>
      ) : (
        <div className="page__feed">
          {/* The cards stay while the next page loads: replacing them with a
              spinner would collapse the list on every page step. */}
          <div className="admin-post-list" aria-busy={refreshing || undefined} ref={feedRef}>
            {rows.map(item => (
              <ReportedContentCard
                key={item.reportedPostId}
                item={item}
                selected={selected.has(item.reportedPostId)}
                pending={bulkPending}
                onToggle={() => toggleRow(item.reportedPostId)}
                onApprove={() => reviewSingle(item, 'approve')}
                onReject={() => reviewSingle(item, 'reject')}
                onHide={() => hideSingle(item)}
                onRestore={() => restoreSingle(item)}
              />
            ))}
            {loadMoreControls ? (
              <div className="admin-post-list__more">{loadMoreControls}</div>
            ) : null}
          </div>
        </div>
      )}

      <ReviewReportsDialog
        intent={reviewIntent}
        items={selectedItems}
        onClose={() => setReviewIntent(null)}
        onReviewed={refreshAfterAction}
      />
    </div>
  );
}
